import { getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page, { PageStyle } from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import { trackUserInfoEvent, trackViewItemEvent } from "@waytek/Handlers/Utilities/CustomTracking";
import Modal, { ModalPresentationProps } from "@waytek/MobiusOverrides/Modal/Modal";
import SchemaForBreadcrumbs from "@waytek/Overrides/Components/SchemaForBreadcrumbs";
import SchemaForOrganization from "@waytek/Overrides/Components/SchemaForOrganization";
import { beginCheckoutTriggered } from "@waytek/Overrides/Pages/CheckoutShippingPage";
import { colorStrings } from "@waytek/Utilities/variables";
import * as React from "react";
import { ReactNode } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
}

/* Verndale Custom Feature - START */
const mapStateToProps = (state: ApplicationState) => {
    const { session } = state.context;
    const billTo = getBillToState(state, session.billToId);
    const shipTo = getShipToState(state, session.shipToId);

    const location = getLocation(state);
    const productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const productState = getProductStateByPath(state, productPath);
    const catalogPageState = getCatalogPageStateByPath(state, productPath);

    return {
        session,
        billToNumber: billTo.value?.customerNumber,
        shipToNumber: shipTo.value?.customerNumber,
        location: billTo.value?.country?.abbreviation ?? "",
        // TODO: Verify location is ok to use country code or page path
        // locationPath: location.pathname,
        website: state.context.website,
        productState,
        catalogPageState,
        locationPath: location.pathname,
        isAuthenticated: session.isAuthenticated,
        pageLoaded: state.context.isWebsiteLoaded,
    };
};

export type Props = PageLayoutProps & ReturnType<typeof mapStateToProps>;

export interface PageLayoutStyles {
    modal?: ModalPresentationProps;
}

export const pageLayoutStyle: PageLayoutStyles = {
    modal: {
        sizeVariant: "small",
    },
};

const styles = pageLayoutStyle;
/* Verndale Custom Feature - END */

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,

    /* Verndale Custom Feature */
    session,
    billToNumber,
    shipToNumber,
    location,
    website,
    productState,
    catalogPageState,
    locationPath,
    isAuthenticated,
    pageLoaded,
}: Props) => {
    const [alreadyTracked, setAlreadyTracked] = React.useState(false);
    /* Verndale Custom Feature */
    const [showLoggedOutModal, setShowLoggedOutModal] = React.useState(false);

    /* Verndale Custom Feature - START */
    React.useEffect(() => {
        setAlreadyTracked(false);
        if (locationPath.indexOf("Cart") > -1) {
            setCookie(beginCheckoutTriggered, "false");
        }
    }, [locationPath]);

    // Track user authentication GTM event:
    React.useEffect(() => {
        if (!session.isAuthenticated || (session.isAuthenticated && billToNumber && shipToNumber)) {
            trackUserInfoEvent({ ...session, billToNumber, shipToNumber, location });
        }
    }, [location, session, billToNumber, shipToNumber]);

    // Track PDP GTM event:
    React.useEffect(() => {
        if (!alreadyTracked && website && productState.value && !productState.isLoading && catalogPageState.value) {
            setAlreadyTracked(true);
            const {
                website: { description: websiteName },
                productState: { value: productInfo },
                catalogPageState: { value: catalogInfo },
            } = { website, productState, catalogPageState };
            const categories =
                catalogInfo.breadCrumbs?.filter(item => item.url != null && item.url !== "/").map(item => item.text) ??
                [];
            trackViewItemEvent(websiteName, productInfo, categories);
        }
    }, [alreadyTracked, website, productState, catalogPageState]);

    React.useEffect(() => {
        const hadActiveSession = getCookie("HadActiveSession");
        if (hadActiveSession == null) {
            setCookie("HadActiveSession", `${isAuthenticated}`);
        } else if (hadActiveSession.toLowerCase() === "true" && !isAuthenticated && pageLoaded) {
            setShowLoggedOutModal(true);
        }
    }, [isAuthenticated, pageLoaded]);

    const handleLoggedOutModalClose = () => {
        setShowLoggedOutModal(false);
        removeCookie("HadActiveSession");
    };
    /* Verndale Custom Feature - END */

    const breadcrumbStyle = css`
        border-bottom: 1px solid ${getColor(colorStrings.commonGlobalIndustrialTaupe)};

        ${PageStyle} {
            padding: 0;
        }

        @media print {
            display: none;
        }
    `;

    return (
        <PageLayoutWrapper>
            <SchemaForBreadcrumbs
                location={[
                    "CategoryDetailsPage",
                    "ProductListPage",
                    "ProductDetailsPage",
                    "BlogLandingPage",
                    "BlogCategoryPage",
                    "BlogDetailPage",
                ]}
                id="SchemaBreadcrumbList"
            />
            <SchemaForOrganization id="SchemaOrganization" />
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {showBreadcrumbs && (
                <StyledWrapper css={breadcrumbStyle}>
                    <Page as="div">
                        <PageBreadcrumbs />
                    </Page>
                </StyledWrapper>
            )}
            {pageContent}

            <Modal
                {...styles.modal}
                headline={siteMessage("UserSession_IsOverTitle")}
                isOpen={showLoggedOutModal}
                handleClose={handleLoggedOutModalClose}
            >
                <StyledWrapper data-test-selector="sessionIsOverModal">
                    {siteMessage("UserSession_IsOverContent")}
                </StyledWrapper>
            </Modal>

            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

/* Verndale Custom Feature */
export default connect(mapStateToProps)(PageLayout);
